import React, { ChangeEvent, useEffect, useState } from "react";
import styles from "./index.module.scss";

interface UploadFieldProps {
  label: string;
  acceptedFileTypes: string[];
  fileSelected?: File | null;
  existingFileName?: string | null;
  existingFileUrl?: string | null;
  onRemoveExisting?: () => void;
  onChange: (file: File | null) => void;
}

const UploadField: React.FC<UploadFieldProps> = ({ 
  label, 
  acceptedFileTypes, 
  fileSelected = null, 
  existingFileName = null,
  existingFileUrl = null,
  onRemoveExisting,
  onChange 
}) => {
  const [fileName, setFileName] = useState<string>("");

  useEffect(() => {
    if (fileSelected instanceof File) {
      setFileName(fileSelected.name);
    } else {
      setFileName("");
    }
  }, [fileSelected]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFileName(file.name);
      onChange(file);
    }
  };

  const handleRemove = () => {
    if (existingFileName && onRemoveExisting) {
      // Remove existing file
      onRemoveExisting();
    } else {
      // Remove selected file
      setFileName("");
      onChange(null);
      // Clear the input value
      const input = document.getElementById(label) as HTMLInputElement;
      if (input) {
        input.value = "";
      }
    }
  };

  const handleDownload = () => {
    if (existingFileUrl) {
      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = existingFileUrl;
      link.download = existingFileName || 'download';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const displayName = fileName || existingFileName;
  const hasFile = fileName || existingFileName;
  const isExistingFile = !fileName && existingFileName;

  return (
    <div className={styles.uploadField}>
      <label htmlFor={label} className={styles.label}>
        {label}
      </label>
      <div className={styles.inputContainer}>
        <input
          type="file"
          accept={acceptedFileTypes.join(",")}
          onChange={handleInputChange}
          className={styles.fileInput}
          id={label}
          name={label}
        />
        <label htmlFor={label} className={`${styles.fileInputLabel} ${isExistingFile ? styles.existingFileLabel : ''}`}>
          <span className={styles.uploadIcon}>📁</span>
          <span className={styles.uploadText}>
            {displayName ? displayName : `Choose ${label}`}
          </span>
          {isExistingFile && (
            <span className={styles.existingBadge}>Current</span>
          )}
        </label>
        {hasFile && (
          <div className={styles.buttonContainer}>
            {isExistingFile && existingFileUrl && (
              <button
                type="button"
                className={styles.downloadButton}
                onClick={handleDownload}
                title="Download file"
              >
                ⬇
              </button>
            )}
            <button
              type="button"
              className={styles.removeButton}
              onClick={handleRemove}
              title={isExistingFile ? "Remove existing file" : "Remove file"}
            >
              ✕
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default UploadField;